import { useCallback, useEffect, useRef, useState } from "react";

// Same voice service the homepage HAILEY uses (REACT_APP_HAILEY_TTS). It answers
// JSON { audio: base64 mp3, starts: [seconds per character] } when timestamps
// are asked for, or raw mp3 bytes otherwise.
const TTS_URL = process.env.REACT_APP_HAILEY_TTS;
const API_KEY = process.env.REACT_APP_HAILEY_KEY;
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=";

/** Text she says out loud: no links or markdown, at most 3 sentences. */
export function speakable(text) {
  let s = String(text || "")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_#`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const sentences = s.match(/[^.!?]+[.!?]+/g);
  if (sentences && sentences.length > 3) s = sentences.slice(0, 3).join(" ").trim();
  return s.slice(0, 600);
}

/**
 * HAILEY's voice for the Creator Partner panel: plays speech, drives the orb
 * (state + live amplitude from the audio) and reports which word she is on.
 *
 * Audio must be unlocked inside a user gesture (`unlock()`), otherwise Safari
 * and Chrome block playback. The amplitude analyser is only attached when the
 * AudioContext is running, so a suspended context can never silence her.
 */
export default function useHaileyVoice() {
  const [orbState, setOrbState] = useState("idle"); // idle | listening | thinking | speaking
  const [amp, setAmp] = useState(0);
  const [speech, setSpeech] = useState({ key: null, n: 0, text: "" });
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const ampRaf = useRef(0);
  const followRaf = useRef(0);
  const unlocked = useRef(false);
  const seq = useRef(0);

  const unlock = useCallback(() => {
    if (unlocked.current) return;
    unlocked.current = true;
    const a = audioRef.current || (audioRef.current = new Audio());
    a.src = SILENT_WAV;
    a.play()?.catch(() => {});
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && !ctxRef.current) ctxRef.current = new AC();
      ctxRef.current?.resume?.();
    } catch (_) {}
  }, []);

  const trackAmplitude = useCallback((el) => {
    try {
      const ctx = ctxRef.current;
      if (!ctx || ctx.state !== "running") return; // never reroute audio into a stopped graph
      if (!analyserRef.current) {
        const src = ctx.createMediaElementSource(el);
        analyserRef.current = ctx.createAnalyser();
        analyserRef.current.fftSize = 256;
        src.connect(analyserRef.current);
        analyserRef.current.connect(ctx.destination);
      }
      const an = analyserRef.current;
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        if (el.paused || el.ended) {
          setAmp(0);
          ampRaf.current = 0;
          return;
        }
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        setAmp(Math.min(1, Math.sqrt(sum / buf.length) * 3.2));
        ampRaf.current = requestAnimationFrame(tick);
      };
      if (!ampRaf.current) ampRaf.current = requestAnimationFrame(tick);
    } catch (_) {
      /* no amplitude: the orb's CSS tremble covers it */
    }
  }, []);

  const stop = useCallback(() => {
    seq.current += 1;
    cancelAnimationFrame(followRaf.current);
    const a = audioRef.current;
    if (a) {
      a.onended = null;
      a.pause();
    }
    setSpeech({ key: null, n: 0, text: "" });
    setAmp(0);
    setOrbState("idle");
  }, []);

  /** Speak `text`; `key` identifies the message so the panel can light its words. */
  const speak = useCallback(
    async (text, key) => {
      const line = speakable(text);
      if (!line || !TTS_URL) return;
      const my = ++seq.current;
      cancelAnimationFrame(followRaf.current);
      setOrbState("speaking");
      try {
        const res = await fetch(TTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(API_KEY ? { "X-InstaPass-Key": API_KEY } : {}) },
          body: JSON.stringify({ role: "instapass", text: line, timestamps: true }),
        });
        if (!res.ok) throw new Error("tts");
        let url;
        let starts = null;
        if ((res.headers.get("content-type") || "").includes("application/json")) {
          const j = await res.json();
          if (!j.audio) throw new Error("tts");
          const bin = atob(j.audio);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          url = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
          starts = Array.isArray(j.starts) ? j.starts : null;
        } else {
          url = URL.createObjectURL(await res.blob());
        }
        if (my !== seq.current) return URL.revokeObjectURL(url); // stopped or replaced meanwhile
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        const a = audioRef.current || (audioRef.current = new Audio());
        a.src = url;
        const done = () => {
          if (my !== seq.current) return;
          cancelAnimationFrame(followRaf.current);
          setSpeech({ key: null, n: 0, text: "" });
          setAmp(0);
          setOrbState("idle");
        };
        a.onended = done;
        a.onerror = done;
        a.onplay = () => {
          trackAmplitude(a);
          const words = line.split(" ").filter(Boolean);
          if (!starts || !starts.length) return;
          const cue = [];
          let at = 0;
          words.forEach((w) => {
            cue.push(starts[Math.min(at, starts.length - 1)] || 0);
            at += w.length + 1;
          });
          let k = 0;
          const tick = () => {
            if (my !== seq.current) return;
            while (k < words.length && cue[k] <= a.currentTime) k += 1;
            setSpeech((p) => (p.key === key && p.n === k ? p : { key, n: k, text: line }));
            if (k < words.length && !a.ended && !a.paused) followRaf.current = requestAnimationFrame(tick);
          };
          followRaf.current = requestAnimationFrame(tick);
        };
        await a.play();
      } catch (_) {
        if (my === seq.current) {
          setSpeech({ key: null, n: 0, text: "" });
          setAmp(0);
          setOrbState("idle");
        }
      }
    },
    [trackAmplitude]
  );

  useEffect(
    () => () => {
      cancelAnimationFrame(ampRaf.current);
      cancelAnimationFrame(followRaf.current);
      audioRef.current?.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      ctxRef.current?.close?.();
    },
    []
  );

  return { orbState, setOrbState, amp, speech, speak, stop, unlock, available: Boolean(TTS_URL) };
}
